import { useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { getEntitlements, trackEvent } from '@surfline/web-common';
import { DisclosureIcon } from '@wavetrak/theme';
import classNames from 'classnames';
import WavetrakLink from 'components/WavetrakLink';
import { getAdvertisingIds } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import { getUserSubdivisionCode, useUserCountryCode } from 'selectors/user';
import { getMostSignificantEntitlement } from 'utils/entitlements';

import styles from './JSBoardBuilderCTA.module.scss';

const JSBoardBuilderCTA = ({ className }: { className?: string }) => {
  const { jsBoardFinderCopy, jsBoardFinderURL } = useAppSelector(getAdvertisingIds);
  const userSubdivisionCode = useAppSelector(getUserSubdivisionCode);
  const userEntitlements = useAppSelector(getEntitlements);
  const userCountryCode = useUserCountryCode();

  const jsBoardFinderURLwithParams = useMemo(() => {
    const baseUrl = jsBoardFinderURL || 'https://bit.ly/jssurflineusa';

    if (!baseUrl) {
      return null;
    }

    const highestEntitlement = getMostSignificantEntitlement(userEntitlements);

    const params = new URLSearchParams();

    if (userSubdivisionCode) {
      params.append('subdivisionCode', userSubdivisionCode);
    }

    if (highestEntitlement) {
      params.append('entitlement', highestEntitlement);
    }

    if (userCountryCode) {
      params.append('countryCode', userCountryCode);
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [jsBoardFinderURL, userSubdivisionCode, userEntitlements, userCountryCode]);

  const handleSegmentEvent = useCallback(() => {
    trackEvent('Clicked CTA', {
      category: 'js surfboards',
      destinationUrl: jsBoardFinderURL,
    });
  }, [jsBoardFinderURL]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleSegmentEvent();
      }
    },
    [handleSegmentEvent],
  );

  if (!jsBoardFinderURLwithParams) {
    return null;
  }

  return (
    <WavetrakLink
      className={classNames(styles.container, className)}
      isExternal
      data-testid="js-board-builder-cta"
      href={jsBoardFinderURLwithParams}
      onClick={handleSegmentEvent}
      onKeyDown={handleKeyDown}
      rel="noopener noreferrer"
      target="_blank"
    >
      <Typography className={styles.ctaText} component="p" variant="footnote">
        {jsBoardFinderCopy}
      </Typography>
      <Box className={styles.logoContainer}>
        <Box className={styles.mockJSLogo} data-testid="js-board-finder-logo" />
        <DisclosureIcon rotation={90} size={14} />
      </Box>
    </WavetrakLink>
  );
};

export default JSBoardBuilderCTA;
